/**
 * Transition bridge: lets a not-yet-native mutation feed the overlay.
 *
 * A native connection mutation puts its operation straight into the mutation cache (see
 * `useConnectionMutation`). A mutation whose server write still runs through Relay's
 * `commitMutation` instead **dispatches** `optimistic`/`completed`/`error` events carrying
 * its `ConnectionOperation`; a single `useConnectionMutationBridge` per connection mirrors
 * each in-flight operation into the mutation cache. Because the coupling lives at the
 * mutation (it emits) and one bridge (it mirrors), every call site feeds the overlay with
 * no per-call-site wiring.
 *
 * Delete this module once every connection mutation is a native `useMutation`.
 */
import {useMutation} from '@github-ui/react-query'
import {useEffect, useEffectEvent, useRef} from 'react'

import {
  type ConnectionOperation,
  connectionPathKey,
  DOCUMENT_FIELDS_PATH,
  type SetFieldsOperation,
} from './connection-operations'
import {notifySettlement} from './settlement-registry'
import {
  type ConnectionMutationVariables,
  connectionMutationKey,
  ENTITY_FIELDS_OWNER,
  entityFieldsMutationKey,
} from './pending-operations'

const CONNECTION_MUTATION_EVENT = 'connection-mutation'

/**
 * One phase of a bridged (non-native) connection mutation. `mutationId` correlates the
 * `optimistic` → `completed`/`error` phases of a single invocation.
 */
export type ConnectionMutationEventDetail =
  | {phase: 'optimistic'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'completed'; mutationId: string; ownerId: string; connectionPath: string[]; operation: ConnectionOperation}
  | {phase: 'error'; mutationId: string; error: Error}

class ConnectionMutationEvent extends Event {
  readonly detail: ConnectionMutationEventDetail
  constructor(detail: ConnectionMutationEventDetail) {
    super(CONNECTION_MUTATION_EVENT)
    this.detail = detail
  }
}

/** The bus bridged mutations dispatch on and `useConnectionMutationBridge` reads. */
const connectionMutationEventTarget: EventTarget = new EventTarget()

let idCounter = 0
/** A fresh correlation id for one bridged mutation invocation. */
export function createConnectionMutationId(): string {
  return `conn-mut-${Date.now().toString(36)}-${(idCounter++).toString(36)}`
}

/**
 * Dispatch one phase of a bridged connection mutation. Producers call this from
 * their Relay `commitMutation` optimistic/onCompleted/onError callbacks so that
 * every call site of the mutation feeds the overlay.
 */
export function dispatchConnectionMutation(detail: ConnectionMutationEventDetail): void {
  connectionMutationEventTarget.dispatchEvent(new ConnectionMutationEvent(detail))
}

// Align an operation's own `connectionPath` to this bridge's key path before it enters the
// mutation cache (optimistic) or a settle fold (completed). `applyOperation(s)` locates the
// connection from the operation's own path, so a producer that emitted a mismatched path could
// otherwise mutate a sibling connection. `setFields` carries no connection path. Mirrors the
// same invariant `useConnectionMutation` enforces on its native producers.
function withKeyAlignedPath(operation: ConnectionOperation, connectionPath: string[]): ConnectionOperation {
  return operation.type === 'setFields' ? operation : {...operation, connectionPath}
}

type Deferred = {promise: Promise<void>; resolve: () => void; reject: (reason?: unknown) => void}
function createDeferred(): Deferred {
  let resolve!: () => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })
  return {promise, resolve, reject}
}

type BridgeVariables = ConnectionMutationVariables & {mutationId: string; settled: Promise<void>}

// A settler plus the completion callback and the connection path captured at optimistic time,
// so a late terminal event folds into the connection the mutation *started* against — not
// whatever `onCompleted`/`connectionPath` this bridge instance has after e.g. navigating to
// another issue or being re-pointed at a different connection.
type Settler = Deferred & {
  onCompleted?: (operation: ConnectionOperation) => void | Promise<void>
  onOutstandingAbandoned?: () => void
  connectionPath: string[]
}

/**
 * Mirrors bridged connection-mutation events for one `{ownerId, connectionPath}` into the mutation
 * cache so `useOptimisticData` overlays them.
 *
 * Mount **once per connection**, not once per document. Confirmed operations reach documents
 * through the settlement registry, so this takes no per-document configuration.
 *
 * No-op when `enabled` is false: no event listener, no mutation-cache writes.
 */
export function useConnectionMutationBridge({
  ownerId,
  connectionPath,
  enabled = true,
}: {
  ownerId: string
  connectionPath: string[]
  enabled?: boolean
}): void {
  // Settlement is not a parameter: confirmed values reach documents through the shared registry
  // `useOptimisticData` writes to. The bridge still *awaits* it, which is what keeps the overlay
  // from dropping before `base` reflects the write.
  const onCompleted = (operation: ConnectionOperation) => notifySettlement(operation, ownerId, connectionPath)
  const onOutstandingAbandoned = undefined
  // mutationId → settler for its terminal event, so concurrent in-flight operations on the
  // same connection don't settle each other.
  const settlers = useRef(new Map<string, Settler>())

  const {mutate} = useMutation<void, unknown, BridgeVariables>({
    mutationKey: connectionMutationKey(ownerId, connectionPath),
    // Return the deferred captured at dispatch (carried on `variables`), not a map lookup.
    // TanStack runs this a microtask after `mutate()`; a terminal event in that gap can delete
    // the settler, and a lookup miss would resolve the mutation as successful and drop the
    // settler's rejection. Reading it from `variables` always reflects the real outcome.
    mutationFn: variables => variables.settled,
  })

  // Arrays are new each render; compare the connection by a stable, collision-free key.
  const pathKey = connectionPathKey(connectionPath)

  // `useEffectEvent` gives a stable handler that always sees the latest props
  // (ownerId, mutate, onCompleted) without re-subscribing the listener each render.
  const handleEvent = useEffectEvent((event: Event) => {
    const {detail} = event as ConnectionMutationEvent

    if (detail.phase === 'optimistic') {
      if (detail.ownerId !== ownerId || connectionPathKey(detail.connectionPath) !== pathKey) return
      // Register the settler before starting the mutation so a fast terminal event can't
      // arrive first. Capture the current `onCompleted`, `onOutstandingAbandoned` and
      // `connectionPath` so a settle (or abandonment) after this bridge re-points at another
      // connection still targets the connection the mutation *started* against.
      const deferred = createDeferred()
      settlers.current.set(detail.mutationId, {...deferred, onCompleted, onOutstandingAbandoned, connectionPath})
      // Carry the deferred promise on the variables so `mutationFn` returns this exact promise.
      // Even if a same-tick terminal event rejects it before `mutationFn` runs, returning it
      // there attaches TanStack's handler in the same microtask checkpoint — so the mutation
      // records the real outcome and the rejection is never unhandled.
      mutate({
        operation: withKeyAlignedPath(detail.operation, connectionPath),
        mutationId: detail.mutationId,
        settled: deferred.promise,
      })
      return
    }

    // completed/error: settle a mutation this bridge started.
    const settler = settlers.current.get(detail.mutationId)
    if (settler) {
      settlers.current.delete(detail.mutationId)
      if (detail.phase === 'completed') {
        // Align to the path captured at optimistic time, not this bridge's current
        // `connectionPath` (which may have changed if it re-pointed mid-flight). Await the fold
        // before resolving: `onCompleted` is typed `void` but may return a promise (an async
        // base refresh), and resolving first would drop the optimistic operation before `base`
        // reflects the write, flashing stale data. If the fold throws or rejects, settle the
        // deferred (reject) instead — otherwise this settler is already removed from the map, so
        // the mutation would stay `pending` forever (stale overlay) and unmount cleanup couldn't
        // reject it. Rejecting drops it from the pending set; the query refetches server truth.
        void (async () => {
          try {
            await settler.onCompleted?.(withKeyAlignedPath(detail.operation, settler.connectionPath))
            settler.resolve()
          } catch (error) {
            settler.reject(error)
          }
        })()
      } else {
        // Reject with the producer's own error so mutation-cache error handlers and diagnostics
        // see the actionable Relay/server cause, not a generic bridge message.
        settler.reject(detail.error)
      }
      return
    }

    // Orphan terminal event: its `optimistic` was registered on a previous bridge instance
    // that unmounted mid-flight (navigate away then back), so there's no settler. A
    // `completed` for this connection must still fold via the current `onCompleted` —
    // otherwise a remount whose refetch raced ahead of the server keeps the stale row until
    // an unrelated refetch. `applyOperation` is idempotent, so a redundant fold is a no-op;
    // an orphan `error` carries no target and its pending operation is already gone.
    if (
      detail.phase === 'completed' &&
      detail.ownerId === ownerId &&
      connectionPathKey(detail.connectionPath) === pathKey
    ) {
      // No settler to resolve/reject here — this is an orphan completion. `onCompleted` may return
      // a promise (an async base refresh); await it in a try/catch so a rejection can't become an
      // unhandled error. If it fails the connection just reconciles on the next refetch/live update.
      void (async () => {
        try {
          await onCompleted?.(withKeyAlignedPath(detail.operation, connectionPath))
        } catch {
          // Swallow: nothing to reject for an orphan, and base converges on the next server sync.
        }
      })()
    }
  })

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const outstanding = settlers.current
    connectionMutationEventTarget.addEventListener(CONNECTION_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => {
      controller.abort()
      // On unmount the terminal event would arrive with no listener, leaving these mutations
      // `pending` forever (a later mount would replay a stale removal). Reject them now so they
      // leave the pending set.
      //
      // A terminal event dispatched while no bridge is mounted is lost (`EventTarget` doesn't
      // buffer), so those operations can't settle by fold and the consumer's base is left stale
      // (a removed row would reappear on remount). Ask the consumer to refresh each affected
      // connection's base — e.g. invalidate its query. Each abandonment callback is captured per
      // settler (like `onCompleted`), so if the bridge was re-pointed while a mutation was still
      // in flight, the connection it *started* against is refreshed too — not just the latest.
      const abandonCallbacks = new Set<() => void>()
      for (const settler of outstanding.values()) {
        if (settler.onOutstandingAbandoned) abandonCallbacks.add(settler.onOutstandingAbandoned)
        settler.reject(new Error('connection mutation bridge unmounted'))
      }
      outstanding.clear()
      for (const refreshBase of abandonCallbacks) refreshBase()
    }
  }, [enabled])
}

/**
 * Mirrors bridged **entity field** events (`DOCUMENT_FIELDS_PATH`) into the mutation cache so
 * `useOptimisticData` overlays them, and notifies the settlement registry when each one confirms.
 *
 * Mount **once for the app**, not once per document. It takes no per-document configuration, and
 * documents receive confirmed values by declaring `targets` on `useOptimisticData`. A second mount
 * is harmless but wasteful: it mirrors the same event again, and every registered document is
 * notified once per mount, so folds repeat (idempotent) and any `revalidate` refetch repeats too.
 */
export function useEntityFieldsBridge({enabled = true}: {enabled?: boolean} = {}): void {
  // Settlement is not a parameter: confirmed values reach documents through the shared registry
  // `useOptimisticData` writes to, exactly as they do for a native producer. That is what lets this
  // mount be global — one per app rather than one per document — and what lets it disappear with
  // Relay without every consumer changing.
  const onCompleted = (operation: SetFieldsOperation) =>
    notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
  const onOutstandingAbandoned = undefined
  const settlers = useRef(new Map<string, Settler>())

  const {mutate} = useMutation<void, unknown, BridgeVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => variables.settled,
  })

  const handleEvent = useEffectEvent((event: Event) => {
    const {detail} = event as ConnectionMutationEvent

    if (detail.phase === 'optimistic') {
      // Entity operations only; structural ones stay with the per-connection bridge, which knows
      // the connection they address.
      if (detail.operation.type !== 'setFields') return
      if (connectionPathKey(detail.connectionPath) !== connectionPathKey(DOCUMENT_FIELDS_PATH)) return

      const deferred = createDeferred()
      settlers.current.set(detail.mutationId, {
        ...deferred,
        onCompleted: onCompleted as Settler['onCompleted'],
        onOutstandingAbandoned,
        connectionPath: DOCUMENT_FIELDS_PATH,
      })
      mutate({operation: detail.operation, mutationId: detail.mutationId, settled: deferred.promise})
      return
    }

    const settler = settlers.current.get(detail.mutationId)
    if (!settler) {
      // Orphan completion: no optimistic phase was registered here. Two real paths reach this —
      // a producer that publishes a confirmed operation without an optimistic one (a mutation
      // committed with no optimistic payload), and a bridge that mounted after the optimistic
      // phase went out (navigate away and back mid-flight). The patch hook this replaced applied
      // completed events unconditionally, so dropping them would leave the document stale until
      // an unrelated refetch. There is nothing pending to settle, so just fold.
      //
      // Filtered on the same path as the optimistic branch above. Without it this bridge would
      // adopt the confirmed half of an operation whose optimistic half it deliberately ignored,
      // which is only currently unreachable because every `setFields` producer publishes on
      // `DOCUMENT_FIELDS_PATH`.
      if (
        detail.phase === 'completed' &&
        detail.operation.type === 'setFields' &&
        connectionPathKey(detail.connectionPath) === connectionPathKey(DOCUMENT_FIELDS_PATH)
      ) {
        void (async () => {
          try {
            await onCompleted?.(detail.operation as SetFieldsOperation)
          } catch {
            // Nothing to reject for an orphan; base converges on the next server sync.
          }
        })()
      }
      return
    }
    settlers.current.delete(detail.mutationId)

    if (detail.phase === 'error') {
      settler.reject(detail.error)
      return
    }

    void (async () => {
      try {
        await settler.onCompleted?.(detail.operation)
        settler.resolve()
      } catch (error) {
        // Leaving it pending would strand the overlay forever; rejecting drops it and the next
        // read of server truth reconciles.
        settler.reject(error)
      }
    })()
  })

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const outstanding = settlers.current
    connectionMutationEventTarget.addEventListener(CONNECTION_MUTATION_EVENT, handleEvent, {
      signal: controller.signal,
    })
    return () => {
      controller.abort()
      const abandonCallbacks = new Set<() => void>()
      for (const settler of outstanding.values()) {
        if (settler.onOutstandingAbandoned) abandonCallbacks.add(settler.onOutstandingAbandoned)
        settler.reject(new Error('entity fields bridge unmounted'))
      }
      outstanding.clear()
      for (const refreshBase of abandonCallbacks) refreshBase()
    }
  }, [enabled])
}

/**
 * Publishes a field edit for one entity and hands back its terminal callbacks.
 *
 * A Relay producer has four lifecycle obligations that are silent when missed: publish the
 * optimistic phase *before* `commitMutation` (the bridge subscribes synchronously, so a mutation
 * settling in the same tick would deliver its terminal first and strand the overlay), reuse one
 * correlation id, publish the server's values on success, and settle on *every* exit — including
 * `onUnsubscribe`, a setup throw, and a response too partial to fold. An unsettled operation is a
 * permanent overlay, not a stale value.
 *
 * Calling this satisfies the first two by construction and leaves the caller only `completed` and
 * `failed` to wire.
 */
export function publishOptimisticFields(
  id: string,
  /**
   * Omit to publish only the confirmed operation. Some producers must not show a locally derived
   * value — `md2html` refuses to parse task-list markdown optimistically, for instance — but still
   * need to fold the server's answer. The completed operation then arrives with no optimistic phase
   * registered, which the entity bridge folds as an orphan completion.
   */
  fields?: Record<string, unknown>,
): {
  completed: (confirmedFields: Record<string, unknown>) => void
  failed: (error: Error) => void
} {
  const mutationId = createConnectionMutationId()
  const dispatch = (phase: 'optimistic' | 'completed', operationFields: Record<string, unknown>) =>
    dispatchConnectionMutation({
      phase,
      mutationId,
      ownerId: id,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: {type: 'setFields', id, fields: operationFields},
    })

  if (fields) dispatch('optimistic', fields)

  return {
    completed: confirmedFields => dispatch('completed', confirmedFields),
    // Harmless when nothing was published: the bridge finds no settler for this id and ignores it.
    failed: error => dispatchConnectionMutation({phase: 'error', mutationId, error}),
  }
}
